import React from "react";
import {
	ApplicationService,
	ContextService,
	EventService,
	FootballService,
	GamedayApiService,
	GameService,
	SeasonService,
	SecurityService,
	SportService,
	StorageService,
	SyncService,
	SyncServiceFactory
} from "@prestosports/prestostats-core/Services";
import { GameAccess } from "@prestosports/prestostats-core/Common";
import { GameRoutes } from "../../routes/game-routes";
import StringFormat from "../../util/string-format";
import {
	GET_APP_DATA,
	GET_APP_DATA_COMPLETE,
	GET_CURRENT_EVENT_SYNC_COMPLETE,
	GET_GAME_ACCESS,
	GET_GAME_ACCESS_COMPLETE,
	GET_GAME_MENU_ITEMS_COMPLETE,
	GET_TEAM_API_ACCESS_COMPLETE,
	ON_ERROR,
	QUEUE_SYNC_COMPLETE,
	RESET_CONFIRM_SETTING_SAVE,
	RESET_GAME_ACCESS,
	SET_ALLOW_EVENT_SETTINGS_EDITING,
	SET_CONFIRM_SAVE,
	SET_CONFIRM_SETTING_SAVE,
	SET_DEBUG_DATA,
	SET_EDIT_PLAY,
	SET_INSERT_PLAY,
	SET_INSERT_TYPE,
	SET_SELECTED_DATE,
	SET_SELECTED_SPORT,
	SUBMITTING,
	SUBMITTING_COMPLETE,
	SYNCING,
	SYNCING_COMPLETE
} from "./constants";
import {
	resetCurrentEvent,
	setRemoteEventChanged,
	updateCurrentEvent
} from "../events/actions";
import { clearGame } from "../game/actions";
import { resetCurrentSeason } from "../season/actions";
import { resetCurrentTeams } from "../teams/actions";
import TeamLogo from "../../views/team/components/TeamLogo";

export const redirect = (history, url) => {
	history(url);
};

const isMenuItemEnabled = (route, gameAccess) => {
	const accessCodes = gameAccess.map((ga) => ga.code);

	if (route.id === GameRoutes.STATS.id) {
		return (
			accessCodes.includes(GameAccess.VIEW_STATS.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.FINALIZE.id) {
		return (
			accessCodes.includes(GameAccess.FINALIZE.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.GAME_MODE.id) {
		return (
			accessCodes.includes(GameAccess.ADD_PLAYS.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.GAME_CONFIG.id) {
		return (
			accessCodes.includes(GameAccess.GAME_CONFIG.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.TEAM_INFORMATION_HOME.id) {
		return (
			accessCodes.includes(GameAccess.HOME_TEAM_INFO.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.TEAM_INFORMATION_VISITOR.id) {
		return (
			accessCodes.includes(GameAccess.AWAY_TEAM_INFO.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	if (route.id === GameRoutes.EVENT_INFORMATION.id) {
		return (
			accessCodes.includes(GameAccess.EVENT_INFO.code) ||
			accessCodes.includes(GameAccess.ALL.code)
		);
	}

	return true;
};

const getTeamIcon = (team) => {
	const { teamName } = team || {};
	return {
		logo: <TeamLogo hideName team={team} size="small" />,
		text: StringFormat.getInitials(teamName)
	};
};

const resolveIcon = (route, homeTeam, awayTeam) => {
	if (route.id === GameRoutes.TEAM_INFORMATION_VISITOR.id) {
		return getTeamIcon(awayTeam);
	}

	if (route.id === GameRoutes.TEAM_INFORMATION_HOME.id) {
		return getTeamIcon(homeTeam);
	}

	return route.icon;
};

export const getCurrentEventSync = async (currentEventId, dispatch) => {
	const currentEventSync = await SyncService.getSyncByEventId(currentEventId);

	dispatch({
		type: GET_CURRENT_EVENT_SYNC_COMPLETE,
		payload: { currentEventSync }
	});
};

export const getApiAccess = async (teams, dispatch) => {
	const apiAccess = {
		teams: teams.map((team) => ({
			id: team.id,
			apiAccess: team.apiAccess
		}))
	};
	await ApplicationService.setApiAccess(apiAccess);

	dispatch({
		type: GET_TEAM_API_ACCESS_COMPLETE,
		payload: { apiAccess }
	});
};

export const getGameMenuItems = async (gameId, gameAccess, dispatch) => {
	const gameMenuItems = [];
	const game = await GameService.getGame(gameId);
	if (game) {
		const event = await EventService.getById(game.eventId);
		const { teams } = event;
		const { homeTeam, awayTeam } = teams;

		Object.values(GameRoutes).forEach((r) => {
			if (r.isNavItem) {
				gameMenuItems.push({
					...r,
					url: r.url.replace(":eventId", event.id),
					disabled: !isMenuItemEnabled(r, gameAccess, event),
					icon: resolveIcon(r, homeTeam, awayTeam)
				});
			}
		});
	}

	dispatch({
		type: GET_GAME_MENU_ITEMS_COMPLETE,
		payload: { gameMenuItems }
	});
};

export const goOnline = async (online) => {
	await GamedayApiService.goOnline(online);
};

export const exportDB = async () => {
	return StorageService.exportDB();
};

export const importDB = async (blob) => {
	return StorageService.importDB(blob);
};

export const clearDB = async () => {
	return StorageService.clearDB();
};

export const initAppData = async (dispatch) => {
	dispatch({ type: GET_APP_DATA });

	const appData = await ApplicationService.getAppData();

	dispatch({
		type: GET_APP_DATA_COMPLETE,
		payload: appData
	});
};

export const getGameAccess = async (gameId, dispatch) => {
	dispatch({ type: GET_GAME_ACCESS });

	const gameAccess = await SecurityService.getGameAccess(gameId);
	getGameMenuItems(gameId, gameAccess, dispatch);

	dispatch({
		type: GET_GAME_ACCESS_COMPLETE,
		payload: { gameAccess: gameAccess.map((ga) => ga.code) }
	});
};

export const setSelectedSport = async (value, dispatch) => {
	await ApplicationService.setSelectedSport(value);
	dispatch({ type: SET_SELECTED_SPORT, payload: { selectedSport: [value] } });
};

export const setSelectedDate = async (value, dispatch) => {
	await ApplicationService.setSelectedDate(value);
	dispatch({ type: SET_SELECTED_DATE, payload: { selectedDate: value } });
};

export const setEditPlay = async (editPlay, dispatch) => {
	dispatch({
		type: SET_EDIT_PLAY,
		payload: {
			editPlay: { ...editPlay }
		}
	});
};

export const setInsertPlay = (insertPlay, dispatch) => {
	dispatch({
		type: SET_INSERT_PLAY,
		payload: { insertPlay }
	});
};

export const setInsertType = async (insertType, dispatch) => {
	dispatch({
		type: SET_INSERT_TYPE,
		payload: { insertType }
	});
};

export const setConfirmSave = async (value, editPlay, dispatch) => {
	const { title, messages } =
		editPlay && value ? await FootballService.generatePlaySummary(editPlay) : {};

	dispatch({
		type: SET_CONFIRM_SAVE,
		payload: {
			confirmSave: value,
			editPlay: { ...editPlay, title, messages }
		}
	});
};

export const withSubmitting = async (fn, dispatch) => {
	try {
		dispatch({
			type: SUBMITTING
		});
		return await fn();
	} finally {
		dispatch({
			type: SUBMITTING_COMPLETE
		});
	}
};

export const withSync = async (
	currentEventId,
	onUnchanged,
	onChanged,
	dispatch
) => {
	const { changed: remoteChanged } = await SyncService.getEventDataChanged(
		currentEventId
	);
	if (remoteChanged) {
		await onChanged?.();
	} else {
		await onUnchanged?.();
	}
	const event = await EventService.getById(currentEventId);
	await updateCurrentEvent(event, dispatch);
	await getCurrentEventSync(currentEventId, dispatch);
	return setRemoteEventChanged(remoteChanged, dispatch);
};

export const withQueuedSync = async (currentEventId, onSync, dispatch) => {
	await onSync?.();
	const { id: gameId, sportCode } = await GameService.getGameByEventId(
		currentEventId
	);
	const syncService = SyncServiceFactory.getInstance(sportCode);
	const eventData = await syncService.collectEventData(gameId);

	await syncService.updateQueuedSync(currentEventId, eventData);

	dispatch({
		type: QUEUE_SYNC_COMPLETE
	});
};

export const performQueuedSync = async (currentEventId, dispatch) => {
	dispatch({
		type: SYNCING,
		payload: { isSyncing: true }
	});
	const { event, changed } = await SyncService.getEventDataChanged(
		currentEventId
	);

	if (!changed) {
		const syncService = SyncServiceFactory.getInstance(event.sportId);
		await syncService.performQueuedSync(currentEventId);
	}

	await updateCurrentEvent(event, dispatch);
	await getCurrentEventSync(currentEventId, dispatch);
	setRemoteEventChanged(changed, dispatch);

	dispatch({
		type: SYNCING_COMPLETE,
		payload: { isSyncing: false }
	});
};

export const resetGameAccess = (dispatch) => {
	dispatch({
		type: RESET_GAME_ACCESS,
		payload: {
			gameAccess: [],
			gameMenuItems: []
		}
	});
};

export const setAllowEventSettingsEditing = async (
	allowEventSettingsEditing,
	dispatch
) => {
	await ApplicationService.setAllowEventSettingsEditing(
		allowEventSettingsEditing
	);

	dispatch({
		type: SET_ALLOW_EVENT_SETTINGS_EDITING,
		payload: {
			allowEventSettingsEditing
		}
	});
};

export const resetAndRedirect = (url, history, dispatch) => {
	clearGame(dispatch);
	resetGameAccess(dispatch);
	resetCurrentEvent(dispatch);
	resetCurrentSeason(dispatch);
	resetCurrentTeams(dispatch);
	redirect(history, url);
};

export const setConfirmSettingSave = async (
	currentEvent,
	currentSeason,
	config,
	dispatch
) => {
	let currentConfig = {};
	let ruleSetId = null;
	let sportId = null;
	if (currentEvent) {
		({ gameConfiguration: currentConfig } =
			await EventService.getGameConfigurationOrDefault(currentEvent.id));
		const { id: gameId } = await GameService.getGameByEventId(currentEvent.id);
		({ ruleSetId } = await ContextService.getGameContext(gameId));
		sportId = currentEvent.sportId;
	} else if (currentSeason) {
		({ gameConfiguration: currentConfig } =
			await SeasonService.getGameConfigurationOrDefault(currentSeason.id));
		sportId = currentSeason.sport?.sportId;
		ruleSetId = currentSeason.ruleSetId;
	}

	if (ruleSetId && sportId) {
		dispatch({
			type: SET_CONFIRM_SETTING_SAVE,
			payload: {
				confirmSettingSave: true,
				settingChangeSummary: SportService.generateChangeSummary(
					sportId,
					ruleSetId,
					currentConfig,
					config
				)
			}
		});
	}
};

export const resetConfirmSettingSave = (dispatch) => {
	dispatch({
		type: RESET_CONFIRM_SETTING_SAVE,
		payload: { confirmSettingSave: false }
	});
};

export const appError = (dispatch) => {
	dispatch({
		type: ON_ERROR
	});
};

export const setDebugData = (debugData, dispatch) => {
	dispatch({
		type: SET_DEBUG_DATA,
		payload: { debugData }
	});
};
